/* eslint-disable camelcase */

import { mustache2SlateValueWithCatch } from 'components/TemplateEditor/mustacheUtils';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { Descendant, Text } from 'slate';
import { omsClient, ImResponse, resolve } from '.';

export interface ImPageResponse {
  pageNum: number;
  pageSize: number;
  totalPages: number;
  totalRows: number;
}

export type FetchShopListResponse = ImResponse<{
  shops: Marketing.Shop[];
}>;

export interface RegionParams {
  region: General.UpperCaseRegion;
}

export interface PlatformRegionParams {
  platform: General.PlatformUpperCase;
  region?: General.UpperCaseRegion;
}

export function fetchShopList(
  scenarioType: Marketing.AllScenarioType,
  params: PlatformRegionParams,
) {
  return resolve(
    omsClient.get<FetchShopListResponse>(`/shop/${scenarioType}`, {
      params,
    }),
  );
}

export function fetchMarketingScenario(
  scenarioType: Marketing.AllScenarioType,
  params: PlatformRegionParams,
) {
  return resolve(
    omsClient.get<ImResponse<Marketing.Scenario>>(`/scenarios/${scenarioType}`, {
      params,
    }),
  );
}

export function fetchMarketingTask(id: number) {
  return resolve(omsClient.get<ImResponse<Marketing.Task>>(`/task/${id}`));
}

export interface CreateMarketingTaskBody
  extends Pick<
    Marketing.Task,
    | 'enable'
    | 'filterItem'
    | 'filterAfterSale'
    | 'praiseTriggerPoint'
    | 'filterHasConversation'
    | 'receptionScoreTaskConfig'
  > {
  region?: General.UpperCaseRegion;
  platform: General.PlatformUpperCase;
  scenarioType: Marketing.AllScenarioType;
  taskName: Marketing.Task['name'];
  storeList: Marketing.Task['stores'];
}

export function createMarketingTask(task: CreateMarketingTaskBody) {
  return resolve(omsClient.post<ImResponse<{ taskId: number }>>('/task', task));
}

export interface UpdateMarketingTaskBody extends CreateMarketingTaskBody {
  taskId: Marketing.Task['id'];
}

export function updateMarketingTask(task: UpdateMarketingTaskBody) {
  return resolve(omsClient.post<ImResponse<{ taskId: number }>>('/task', task));
}

export function deleteMarketingTask(id: number) {
  return resolve(omsClient.delete(`/task/${id}`));
}

export interface CreateMarketingTaskRoundBody
  extends Omit<Marketing.Round, 'round' | 'id' | 'noValidPolicy'> {
  taskId: number;
  copyRoundId?: number;
}

export function createMarketingTaskRound(body: CreateMarketingTaskRoundBody) {
  return resolve(omsClient.put('/round', body));
}

export interface UpdateMarketingTaskRoundBody
  extends Omit<CreateMarketingTaskRoundBody, 'copyRoundId'> {
  roundId: number;
}

export function updateMarketingTaskRound(body: UpdateMarketingTaskRoundBody) {
  return resolve(omsClient.put('/round', body));
}

export function deleteMarketingTaskRound(id: Marketing.Round['id']) {
  return resolve(omsClient.delete(`/round/${id}`));
}

export function isValidContent(content: Descendant[]): boolean {
  return content.some((segment) => {
    if (Text.isText(segment) && segment.text) {
      return true;
    }

    if (segment.type === CustomSlateNodeType.PARAGRAPH) {
      return isValidContent(segment.children);
    }

    return false;
  });
}

function removeTranslatedContent(context: Marketing.ExecuteContext) {
  const { content = '', translatedContent = '' } = context;

  const translatedSlateValue = mustache2SlateValueWithCatch(
    translatedContent.replace(/^#mustache#/, ''),
  );

  if (translatedSlateValue && isValidContent(translatedSlateValue)) {
    return {
      content: translatedContent,
      translatedContent: '',
    };
  }

  return {
    content,
    translatedContent: '',
  };
}

export function transferPolicyContent<
  T extends Pick<
    Marketing.Policy,
    | 'bilingualExecuteContexts'
    | 'regionExecuteContexts'
    | 'cancelTradeConfig'
    | 'questionnaireExecuteContext'
  >,
>(policy: T): T {
  const {
    bilingualExecuteContexts,
    regionExecuteContexts,
    cancelTradeConfig,
    questionnaireExecuteContext,
  } = policy;

  return {
    ...policy,
    bilingualExecuteContexts: bilingualExecuteContexts?.map(removeTranslatedContent),
    regionExecuteContexts: regionExecuteContexts?.map(({ executeContext, ...others }) => {
      return {
        ...others,
        executeContext: removeTranslatedContent(executeContext),
      };
    }),
    cancelTradeConfig: cancelTradeConfig
      ? {
          ...cancelTradeConfig,
          contents: cancelTradeConfig.contents?.map(({ executeContext, ...others }) => {
            return {
              ...others,
              executeContext: removeTranslatedContent(executeContext),
            };
          }),
        }
      : undefined,
    questionnaireExecuteContext: questionnaireExecuteContext
      ? {
          ...questionnaireExecuteContext,
          questionnaireRegionExecuteContexts:
            questionnaireExecuteContext.questionnaireRegionExecuteContexts.map((_) => {
              return {
                ..._,
                replyOptionExecuteContexts: _.replyOptionExecuteContexts.map((_) => {
                  return {
                    ..._,
                    executeContext: removeTranslatedContent(_.executeContext),
                  };
                }),
              };
            }),
        }
      : undefined,
  };
}

export function fetchMarketingPolicy(id: Marketing.Policy['id']) {
  return resolve(omsClient.get<ImResponse<{ policy: Marketing.Policy }>>(`/policy/${id}`)).then(
    ({ policy }) => ({ policy: transferPolicyContent(policy) }),
  );
}

export interface FetchMarketingPolicyListParams {
  pageNum: number;
  pageSize: number;
  roundId: number;
  orderBy: Marketing.PolicyOrderBy;
  onlyValid: boolean;
}

export type FetchMarketingPolicyListResponse = {
  pages: ImPageResponse;
  policy: Marketing.Policy[];
};

export function fetchMarketingPolicyList(params: FetchMarketingPolicyListParams) {
  return resolve(
    omsClient.get<ImResponse<FetchMarketingPolicyListResponse>>('/policy', {
      params,
    }),
  ).then(({ policy, ...others }) => {
    return {
      ...others,
      policy: policy.map(transferPolicyContent),
    };
  });
}

export function createMarketingPolicy(body: Omit<Marketing.Policy, 'id' | 'priority'>) {
  return resolve(omsClient.post<ImResponse<never>>('/policy', body));
}

export function updateMarketingPolicy(body: Omit<Marketing.Policy, 'priority'>) {
  return resolve(omsClient.post<ImResponse<never>>('/policy', body));
}

export function deleteMarketingPolicy(id: Marketing.Policy['id']) {
  return resolve(omsClient.delete<ImResponse<never>>(`/policy/${id}`));
}

export type UpdatePriorityType = 'up' | 'down' | 'top' | 'bottom';

export function updateMarketingPolicyPriority(
  id: Marketing.Policy['id'],
  action: UpdatePriorityType,
) {
  return resolve(
    omsClient.post<ImResponse<never>>(`/policy/${id}`, null, {
      params: {
        action,
      },
    }),
  );
}

export interface UnsetStore extends Marketing.TaskStore {
  region: General.UpperCaseRegion;
  externalStoreId: string;
}

export function unsetStoreList(
  scenarioType: Marketing.AllScenarioType,
  params: PlatformRegionParams,
) {
  return resolve(
    omsClient.get<ImResponse<UnsetStore[]>>(`/reminder/store/unset/${scenarioType}`, {
      params,
    }),
  );
}

export interface MoveStoresBody {
  taskId: Marketing.Task['id'];
  stores: UnsetStore[];
}

export function moveStores(body: MoveStoresBody) {
  return resolve(omsClient.put('/reminder/store/move', body));
}

export function getNoValidPolicyRegions(taskId: string | number) {
  return resolve(
    omsClient.get<ImResponse<{ noValidPolicyRegions: (General.UpperCaseRegion | 'ALL')[] }>>(
      `/reminder/check/task/${taskId}`,
    ),
  );
}

export interface FetchMarketingOrderStatParams {
  start: string;
  end: string;
  storeIds: string;
  taskIds: string;
}

export interface FetchMarketingOrderTotalStatParams
  extends FetchMarketingOrderStatParams,
    RegionParams {}

export function fetchMarketingOrderTotalStat(
  scenario: Marketing.ScenarioType,
  params: FetchMarketingOrderTotalStatParams,
) {
  return resolve(
    omsClient.get<ImResponse<Marketing.Stat.Total>>(`/stats/scenarios/${scenario}`, {
      params,
    }),
  );
}

export interface FetchMarketingOrderDimensionStatParams
  extends FetchMarketingOrderStatParams,
    RegionParams {}

export function fetchMarketingOrderDimensionStat(
  scenario: Marketing.ScenarioType,
  dimension: Marketing.Stat.DimensionType,
  params: FetchMarketingOrderDimensionStatParams,
) {
  return resolve(
    omsClient.get<ImResponse<Marketing.Stat.Dimension[]>>(
      `/stats/scenarios/${scenario}/${dimension}`,
      {
        params,
      },
    ),
  );
}

export interface FetchMarketingOrderDetailStatParams
  extends FetchMarketingOrderStatParams,
    RegionParams {
  pageNum: number;
  pageSize: number;
}

export function fetchMarketingOrderDetailStat(
  scenario: Marketing.ScenarioType,
  params: FetchMarketingOrderDetailStatParams,
) {
  return resolve(
    omsClient.get<
      ImResponse<{
        pages: ImPageResponse;
        statsDetail: Marketing.Stat.Detail[];
      }>
    >(`/stats/scenarios/${scenario}/detail`, {
      params,
    }),
  );
}

export interface PrepareDownloadMarketingOrderDetailStatParams
  extends FetchMarketingOrderStatParams,
    RegionParams {}

export function prepareDownloadMarketingOrderDetailStat(
  scenario: Marketing.ScenarioType,
  params: PrepareDownloadMarketingOrderDetailStatParams,
) {
  return resolve(
    omsClient.get<ImResponse<Marketing.Stat.DownloadStatus>>(
      `/stats/scenarios/${scenario}/detail/download/prepare`,
      {
        params,
      },
    ),
  );
}

export function fetchMarketingOrderDetailStatDownloadStatus(taskId: string) {
  return resolve(
    omsClient.get<ImResponse<Marketing.Stat.DownloadStatus>>(
      `/stats/detail/download/tasks/${taskId}/info`,
    ),
  );
}

export function fetchMarketingOrderStatQueryTaskList(
  scenario: Marketing.ScenarioType,
  params: RegionParams,
) {
  return resolve(
    omsClient.get<ImResponse<Marketing.Stat.QueryTask[]>>(`/stats/tasks/${scenario}`, {
      params,
    }),
  );
}

export function fetchMarketingRegionList() {
  return resolve(
    omsClient.get<
      ImResponse<
        {
          region: General.UpperCaseRegion;
          isAuthorized: boolean;
        }[]
      >
    >('/region/authorize-info'),
  );
}

export interface ValidStatusParams {
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
}

export interface ValidStatus {
  region: General.UpperCaseRegion;
  taskAllInvalid: boolean;
}

export function validStatus(params: ValidStatusParams) {
  const { scenarioType, ...others } = params;
  return resolve(
    omsClient.get<ImResponse<ValidStatus[]>>(`/scenarios/status/${scenarioType}`, {
      params: others,
    }),
  );
}

export interface ValidStarsBody {
  taskId: number;
  stars: number[];
}

export function validStars(body: ValidStarsBody) {
  return resolve(
    omsClient.post<
      ImResponse<{ star: number; conflictScenarios: Marketing.ReviewManagementScenarioType[] }[]>
    >('/review/star/conflict', body),
  );
}

export interface IUploadNotify {
  resourceId: number;
  platform: string;
  ossUrl: string;
}

export function uploadNotify(body: IUploadNotify) {
  return resolve(omsClient.post('/upload/notify', body));
}

export interface DetectConflictBody {
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  scenarioType: Marketing.AllScenarioType;
  stores: Marketing.TaskStore[];
}

export interface DetectConflictResponse {
  hasConflict: boolean;
  conflictTasks: {
    taskId: number;
    taskName: string;
  }[];
}

export function detectConflict(body: DetectConflictBody) {
  return resolve(
    omsClient.post<ImResponse<DetectConflictResponse>>('/reminder/signPraiseConflict', body),
  );
}

type TaskStatus = 'PENDING' | 'ONGOING' | 'FINISHED';
/**
  PENDING,  // 待解析
  PARSING,  // 解析中
  FINISH,   // 已完成
  FAILED;   // 失败
*/
type ExcelParseStatus = 'PENDING' | 'PARSING' | 'FINISH' | 'FAILED';

export interface IGetActiveTaskByTypeBody {
  platform: General.PlatformUpperCase;
  pageNum: number;
  pageSize: number;
}
export interface IGetActiveTaskByTypeItem {
  taskId: string;
  taskName: string;
  regionExecuteContexts: {
    enable: boolean;
    region: General.UpperCaseRegion;
    executeContext: {
      content: string;
      translatedContent: string;
    };
  }[];
  regions: General.UpperCaseRegion[];
  status: TaskStatus;
  /** 已发送 */
  dispatched: number;
  updateAt: string;
  /** 是否过滤SIP店铺 */
  filterSIP: true;
  /** 解析ID，获取解析结果时需要。查看详情才会填充 */
  parseId: string;
  /** 原文件的oss url。查看详情才会填充 */
  originUrl: string;
  parseResult: {
    /** 应发送 / 解析成功的条数 */
    planned: number;
    /** 错误的总数 */
    errorCount: number;
    status: ExcelParseStatus;
    /** 带错误标注的excel链接 */
    errorUrl: string;
    originUrl: string;
    regions: General.UpperCaseRegion[];
    tradeIds: string;
    parseErrorVoList: {
      tradeId: string;
      errorMsgLangMap: Record<General.SystemLanguage, string>;
    }[];
  };
  creatorName: string;
  aiPauseTime: number;
}
export interface IGetActiveTaskByTypeRsp {
  pages: ImPageResponse;
  data: IGetActiveTaskByTypeItem[];
}

export function activeParse(body: {
  platform: string;
  ossUrl?: string;
  lang: General.SystemLanguage;
  scenarioType: Marketing.StockoutScenarioType;
  tradeIds?: string;
}) {
  return resolve(omsClient.post<ImResponse<{ parseId: string }>>('/active/parse', body));
}

export interface IActiveParseResultRsp {
  status: ExcelParseStatus;
  /** 应发送 / 解析成功的条数 */
  planned: number;
  errorCount: number;
  originUrl: string;
  errorUrl: string;
  regions: General.UpperCaseRegion[];
  tradeIds: string;
  parseErrorVoList: {
    tradeId: string;
    errorMsgLangMap: Record<General.SystemLanguage, string>;
  }[];
}
export function activeParseResult(parseId: string) {
  return resolve(omsClient.get<ImResponse<IActiveParseResultRsp>>(`/active/parse/${parseId}`));
}

export interface IActiveTaskReq {
  scenarioType: Marketing.StockoutScenarioType;
  platform: General.PlatformUpperCase;
  taskName: string;
  regionExecuteContexts: {
    enable: boolean;
    region: General.UpperCaseRegion;
    executeContext: {
      content: string;
      translatedContent: string;
    };
  }[];
  filterSIP: boolean;
  parseId: number;
  start: boolean;
}
export function activeTaskCreate(body: IActiveTaskReq) {
  return resolve(omsClient.post<ImResponse<IGetActiveTaskByTypeItem>>('/active/task', body));
}

export function activeTaskUpdate(taskId: string, body: IActiveTaskReq) {
  return resolve(
    omsClient.put<ImResponse<IGetActiveTaskByTypeItem>>(`/active/task/${taskId}`, body),
  );
}

export function activeTaskGet(taskId: string) {
  return resolve(
    omsClient.get<ImResponse<IGetActiveTaskByTypeItem>>(`/active/task/${taskId}`),
  ).then(({ regionExecuteContexts, ...others }) => {
    return {
      ...others,
      regionExecuteContexts: regionExecuteContexts.map(({ executeContext, ...others }) => {
        return {
          ...others,
          executeContext: removeTranslatedContent(executeContext),
        };
      }),
    };
  });
}

export function activeConflict(params: {
  name: string;
  platform: General.PlatformUpperCase;
  taskId?: string;
  scenarioType: Marketing.StockoutScenarioType;
}) {
  return resolve(
    omsClient.get<ImResponse<{ nameConflict: boolean }>>(`/active/conflict`, {
      params,
    }),
  );
}

export interface SignalListParams {
  platform: General.PlatformUpperCase;
}

export function signalList(params: SignalListParams) {
  return resolve(
    omsClient.get<ImResponse<{ data: Marketing.SignalTaskDetail[] }>>('/signal/task/list', {
      params,
    }),
  ).then(({ data }) => data);
}

export function signal(id: string | number) {
  return resolve(omsClient.get<ImResponse<Marketing.SignalTaskDetail>>(`/signal/task/${id}`));
}

export function createSignal(body: Omit<Marketing.SignalTask, 'taskId'>) {
  return omsClient.post('/signal/task', body);
}

export function updateSignal(body: Marketing.SignalTask) {
  return omsClient.post('/signal/task', body);
}

export function deleteSignal(id: string | number) {
  return omsClient.delete(`/signal/${id}`);
}

export interface ToggleSignalBody {
  taskId: number;
  enable: boolean;
}

export function toggleSignal(body: ToggleSignalBody) {
  return omsClient.post('/signal/task/enable', body);
}

export function signalRecord(id: string | number) {
  return resolve(omsClient.get<ImResponse<string>>(`/signal/coupon/export/${id}`));
}
